import React, { Fragment } from 'react';
import { useBoolean } from 'usehooks-ts';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectBasicInfoStatus,
  selectUserData,
} from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentStatus,
  RequiredDocumentSection,
} from '@/types/document';

import {
  isCaymanUser,
  isExistingCustomer,
  isRemediationCustomer,
} from '@/utils/user';

import { ReviewEmploymentInfoDrawer } from '../components/PersonalDetails/components/Review/ReviewEmploymentInfoDrawer';
import { POEDocSelectDrawer } from '../drawers/ProofOfEmployment/POEDocSelectDrawer';

export function ProofOfEmployment() {
  const { t } = useTranslate();

  const {
    value: isRequiredDocumentOpen,
    setTrue: openRequiredDocumentDrawer,
    setFalse: closeRequiredDocumentDrawer,
  } = useBoolean(false);
  const {
    value: isReviewEmploymentDrawerOpen,
    setTrue: openReviewEmploymentDrawer,
    setFalse: closeReviewEmploymentDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isRequiredDocumentOpen || isReviewEmploymentDrawerOpen);

  const { user, requiredDocumentSections } = useAppSelector(selectUserData);

  const defaultStatus =
    (isExistingCustomer(user) || isRemediationCustomer(user)) &&
    user?.employmentDetailed?.poeRequired === false
      ? DocumentStatus.Approved
      : DocumentStatus.Pending;

  const proofOfEmploymentSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.ProofOfEmployment,
  ) || {
    type: DocumentSection.ProofOfEmployment,
    displayText: 'documentSection.Proof_of_Employment.title',
    status: defaultStatus,
    docs: [],
  };

  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const isBasicInfoProcessed =
    basicInfoStatus === DocumentStatus.InReview ||
    basicInfoStatus === DocumentStatus.Approved ||
    basicInfoStatus === DocumentStatus.Rejected;
  const isCayman = isCaymanUser(user);
  const onClick = () => {
    if (proofOfEmploymentSection.status === DocumentStatus.Approved) {
      openReviewEmploymentDrawer();
      return;
    }
    openRequiredDocumentDrawer();
  };

  const onEmploymentInfoClose = () => {
    closeReviewEmploymentDrawer();
  };

  const checkBtnDisableForEXC = (
    section: RequiredDocumentSection,
    isNotPendingAndNoDocs: boolean,
  ) => {
    if (user?.clientReviewed) {
      if (section.status === DocumentStatus.Approved) {
        return true;
      }
      /** If the EXC has already signed/uploaded section docs then set it true, otherwise set it false */
      return isNotPendingAndNoDocs;
    } else {
      return true;
    }
  };

  const isButtonDisabled = (section: RequiredDocumentSection) => {
    if (!user) return true;
    const isNotPendingAndNoDocs =
      section.status !== DocumentStatus.Pending && !section.docs.length;
    if (user && isExistingCustomer(user)) {
      return checkBtnDisableForEXC(section, isNotPendingAndNoDocs);
    }

    return isNotPendingAndNoDocs;
  };

  return (
    <Fragment key={DocumentSection.ProofOfEmployment}>
      <DocumentActionItem
        title={t('documentSection.Proof_of_Employment.title')}
        description={t('status.pending')}
        onClick={onClick}
        status={proofOfEmploymentSection.status}
        enabledForAllStatuses
        disabled={
          (user && isExistingCustomer(user) && !user.clientReviewed) ||
          (!isBasicInfoProcessed && !isCayman)
        }
        disabledButton={isButtonDisabled(proofOfEmploymentSection)}
      />
      <POEDocSelectDrawer
        open={isRequiredDocumentOpen}
        section={proofOfEmploymentSection}
        onClose={closeRequiredDocumentDrawer}
      />
      <ReviewEmploymentInfoDrawer
        isReviewEmploymentDrawerOpen={isReviewEmploymentDrawerOpen}
        isProofOfEmploymentApproved={
          proofOfEmploymentSection.status === DocumentStatus.Approved
        }
        onClose={onEmploymentInfoClose}
      />
    </Fragment>
  );
}

export default React.memo(ProofOfEmployment);
